import React from 'react';
import { getIn, useFormikContext } from 'formik';
import isArray from 'lodash/isArray';

import type { CheckboxChangeEvent } from '~/components/ui/atoms/Checkbox';
import executeOrDelegate from '~/Utils/executeOrDelegate';

import CheckboxWithIcon from '../CheckboxWithIcon';

interface CheckboxWithButtonWrapperFormikProps {
  icon?: React.ReactNode;
  text: string;
  /**
   * The entry this checkbox contributes to a list-valued field. Omitted when the field is a plain
   * boolean, which the implementation below already handles by toggling the field itself.
   */
  value?: string;
  disabled?: boolean;
  id: string;
  onChecked?: (event: CheckboxChangeEvent) => void;
  handleChangeWrapper?: (func: () => void) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // For other props - should be removed after validating no unknown props are passed
}

const CheckboxWithButtonWrapperFormik: React.FC<CheckboxWithButtonWrapperFormikProps> = ({
  icon,
  text,
  value,
  disabled,
  id,
  onChecked,
  handleChangeWrapper, // accepts a change func that can be called or not after performing other logic
  ...rest
}) => {
  const { setFieldValue, values } = useFormikContext();

  const onClick = (event: CheckboxChangeEvent) => {
    executeOrDelegate({
      primaryTask: () => {
        const existingValues = getIn(values, id);

        if (isArray(existingValues)) {
          const currValue = [...existingValues];
          const checked = !(currValue.indexOf(value) > -1);
          if (checked) {
            currValue.push(value);
          } else {
            currValue.splice(currValue.indexOf(value), 1);
          }

          setFieldValue(id, currValue);
        } else {
          setFieldValue(id, !existingValues);
        }

        if (onChecked) {
          onChecked(event);
        }
      },
      wrapperFunction: handleChangeWrapper,
    });
  };

  const isChecked = (value?: string) => {
    const existingValues = getIn(values, id);

    if (isArray(existingValues)) {
      return existingValues.indexOf(value) > -1;
    }

    return existingValues;
  };

  return (
    <CheckboxWithIcon
      icon={icon}
      text={text}
      disabled={disabled}
      checked={isChecked(value)}
      onChange={(event: CheckboxChangeEvent) => !disabled && onClick(event)}
      {...rest}
    />
  );
};

export default CheckboxWithButtonWrapperFormik;
